import { useApolloClient } from "@apollo/client/react";
import { isEmpty } from "lodash";
import {
  Button,
  Flex,
  Modal,
  H3,
  Box,
  P,
  Popover,
  Small,
  Divider,
  Notification,
} from "orcs-design-system";
import pluralize from "pluralize";
import PropTypes from "prop-types";
import React, { useState, useMemo } from "react";
import styled from "styled-components";

import { useCachedTag } from "src/hooks/useCachedTag";
import { useCachedGroup } from "src/hooks/useSearchGroups";
import { useCachedPerson } from "src/hooks/useSearchPeople";
import useUpdateEffect from "src/hooks/useUpdateEffect";
import ErrorNotification from "src/components/ErrorNotification";

import {
  formatMissingRequiredAttributesMessage,
  getMissingRequiredAttributeLabels,
  useTagFormValidator,
} from "src/hooks/useTagFormValidator";
import { RichTextContentViewer } from "src/shared/RichTextContent/RichTextContent";
import { getUpdatedAttributes } from "src/components/TagsEditor/utils/getUpdatedAttributes";
import { ATTRIBUTE_ROW_TYPE } from "src/pages/Workspaces/consts";
import { TAG_ATTRIBUTE_ACTION } from "src/consts/tags";
import {
  updateTagValue as updateTagValueMutation,
  updateEntityTag as updateEntityTagMutation,
} from "../queries/tags.graphql";
import {
  overlayMutate,
  updateEntityTagWithOption,
  updateTagValueWithOption,
  useOverlayOptionId,
} from "src/pages/Designs/overlay";
import {
  isTagDisplayValueEditable,
  updateTagsForEntityCache,
} from "../TagsEditor.util";
import { updateTagValueInCache } from "../updateTagValueInCache";

import { TAG_UPDATE_TYPE } from "../consts";
import {
  buildDisplayValue,
  filterTagAttributeTypes,
  isAttributesChanged,
} from "./TagAttributeModal.logic";
import TagAttributesFields, { AdjustedTextInput } from "./TagAttributesFields";

const DetailsWrapper = styled(Box)`
  overflow-y: auto;
  max-height: 150px;
`;

const EmptyAttrTypes = [];

const TagAttributesModal = ({
  onClose,
  entityTag,
  tagTypeConfig,
  isNewTag,
  setNewTag,
  comments,
  entityId,
  tagTypes,
  featureFlags,
  tagTypesConfig,
  editMode = false,
  readOnly = false,
  entityType,
  onTagUpdated,
}) => {
  const [displayValue, setDisplayValue] = useState(entityTag.displayValue);
  const [updatedAttributes, setUpdatedAttributes] = useState(
    getUpdatedAttributes({
      tagType: tagTypeConfig,
      entityId,
      attributes: entityTag.attributes || {},
      typeKeys: [ATTRIBUTE_ROW_TYPE.others, ATTRIBUTE_ROW_TYPE.values],
    })
  );
  const [updatedEntityTagAttributes, setUpdatedEntityTagAttributes] = useState(
    entityTag.entityTagAttributes || {}
  );
  const { getCachedGroup } = useCachedGroup();
  const { getCachedPerson } = useCachedPerson();
  const { getCachedTag } = useCachedTag();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [requiredFieldsMessage, setRequiredFieldsMessage] = useState(null);

  const includeTagLevelAttributes = editMode || isNewTag;

  const attributeValues = {
    ...updatedAttributes,
    ...updatedEntityTagAttributes,
  };

  const {
    onFieldUpdate,
    onFormSave,
    fieldToBeFocused,
    fieldsToShowInvalidity,
    clearFieldToBeFocused,
  } = useTagFormValidator({
    fields: attributeValues,
    tagType: tagTypeConfig,
    tagValue: {
      ...entityTag,
      attributes: attributeValues,
    },
    includeEntityAttributes: true,
    // View/info mode can still edit unique entity-tag attrs; tag-level
    // fields (including required parent tags like EI) stay disabled.
    includeTagLevelAttributes,
  });

  const {
    name: tagTypeName,
    hasDetails,
    detailsParsed: parsedDetails,
    attributes: meta = {},
  } = tagTypeConfig || {};

  const client = useApolloClient();
  const overlayOptionId = useOverlayOptionId();
  const updateEntityTag = async (options) => {
    const result = await overlayMutate({
      client,
      overlayOptionId,
      liveMutation: updateEntityTagMutation,
      overlayMutation: updateEntityTagWithOption,
      variables: options.variables,
      update: updateTagsForEntityCache({
        entityId,
        tagTypes,
        entityTag,
        entityType,
      }),
    });
    if (onTagUpdated && result.data?.tag) {
      onTagUpdated({
        type: TAG_UPDATE_TYPE.ENTITY_TAG,
        payload: result.data.tag,
      });
    }
    return result;
  };

  const {
    separator = "_",
    values: nameAttrTypes = EmptyAttrTypes,
    others: otherAttrTypes = EmptyAttrTypes,
    entityTag: entityTagAttrTypes = EmptyAttrTypes,
    parentTag = EmptyAttrTypes,
  } = meta || {};

  const isDisplayValueEditable = isTagDisplayValueEditable(
    nameAttrTypes,
    otherAttrTypes,
    tagTypeConfig
  );

  const [readonlyAttrTypes, editableAttrTypes, uniqueAttrTypes] =
    useMemo(() => {
      return filterTagAttributeTypes(
        nameAttrTypes,
        otherAttrTypes,
        entityTagAttrTypes
      );
    }, [nameAttrTypes, otherAttrTypes, entityTagAttrTypes]);

  useUpdateEffect(() => {
    if (isEmpty(nameAttrTypes)) {
      return;
    }
    const combinedValue = buildDisplayValue({
      attrTypeValues: nameAttrTypes,
      attributes: updatedAttributes,
      separator,
      getCachedGroup,
      getCachedPerson,
      getCachedTag,
      entityId,
    });
    setDisplayValue(combinedValue);
  }, [
    nameAttrTypes,
    separator,
    updatedAttributes,
    getCachedGroup,
    getCachedPerson,
    getCachedTag,
    entityId,
  ]);

  const onDisplayValueChange = (e) => {
    setDisplayValue(e.target.value);
  };

  const onAttrValueChange = (type, isEntityTagAttr) => (e) => {
    const newValue = e.target.value;
    let targetAttributes = updatedAttributes;
    let updateMethod = setUpdatedAttributes;

    if (isEntityTagAttr) {
      targetAttributes = updatedEntityTagAttributes;
      updateMethod = setUpdatedEntityTagAttributes;
    }
    const newAttributes = {
      ...targetAttributes,
      [type]: newValue,
    };

    updateMethod(newAttributes);
    onFieldUpdate(newAttributes);
    clearFieldToBeFocused();
    setRequiredFieldsMessage(null);
  };

  const onSelectAttrValue = (id, isEntityTagAttr) => (option) => {
    let targetAttributes = updatedAttributes;
    let updateMethod = setUpdatedAttributes;

    if (isEntityTagAttr) {
      targetAttributes = updatedEntityTagAttributes;
      updateMethod = setUpdatedEntityTagAttributes;
    }

    const newAttributes = {
      ...targetAttributes,
      [id]: option ? option.value : undefined,
    };

    updateMethod(newAttributes);
    onFieldUpdate(newAttributes);
    clearFieldToBeFocused();
    setRequiredFieldsMessage(null);
  };

  const onDateChange = (id, isEntityTagAttr) => (value) => {
    let targetAttributes = updatedAttributes;
    let updateMethod = setUpdatedAttributes;

    if (isEntityTagAttr) {
      targetAttributes = updatedEntityTagAttributes;
      updateMethod = setUpdatedEntityTagAttributes;
    }

    const newAttributes = {
      ...targetAttributes,
      [id]: value,
    };
    updateMethod(newAttributes);
    onFieldUpdate(newAttributes);
    clearFieldToBeFocused();
    setRequiredFieldsMessage(null);
  };

  const onSave = async () => {
    const invalidFields = onFormSave({ attributes: attributeValues });

    if (!isEmpty(invalidFields)) {
      setRequiredFieldsMessage(
        formatMissingRequiredAttributesMessage(
          getMissingRequiredAttributeLabels(
            invalidFields,
            tagTypeConfig,
            true,
            includeTagLevelAttributes
          )
        )
      );
      return;
    }

    setRequiredFieldsMessage(null);

    setIsSaving(true);
    try {
      let didSaveTagValue = false;
      if (isNewTag) {
        await setNewTag(
          {
            ...entityTag,
            displayValue,
            attributes: updatedAttributes,
          },
          tagTypeConfig
        );
      } else if (
        editMode &&
        (isAttributesChanged(entityTag?.tag?.attributes, updatedAttributes) ||
          displayValue !== entityTag?.displayValue)
      ) {
        const { data } = await overlayMutate({
          client,
          overlayOptionId,
          liveMutation: updateTagValueMutation,
          overlayMutation: updateTagValueWithOption,
          variables: {
            input: {
              id: entityTag.tagId,
              type: tagTypeConfig.id,
              displayValue,
              isActive: true,
              attributes: updatedAttributes,
            },
          },
          update: (cache) => {
            updateTagValueInCache(cache, {
              tagId: entityTag.tagId,
              attributes: updatedAttributes,
              displayValue,
              entityId,
              tagTypes,
              entityType,
            });
          },
        });
        didSaveTagValue = true;
        if (onTagUpdated && data?.tag) {
          onTagUpdated({
            type: TAG_UPDATE_TYPE.TAG,
            payload: data.tag,
          });
        }
      }

      if (
        entityTag?.id &&
        isAttributesChanged(
          entityTag.entityTagAttributes,
          updatedEntityTagAttributes
        )
      ) {
        await updateEntityTag({
          variables: {
            input: {
              id: entityTag.id,
              tagType: tagTypeConfig.id,
              attributes: updatedEntityTagAttributes,
              comments: {
                ...comments,
                tag: {
                  tagId: entityTag.tagId,
                  tagType: entityTag.type,
                  displayValue: entityTag.displayValue,
                },
              },
            },
          },
        });
      }

      // Re-assert only values persisted by updateTag this save. A trailing
      // write after entity-tag-only saves can leak locally synthesized
      // attributes (e.g. injected ENTITY_ID) into the shared Tag cache.
      const savedTagId = entityTag.tagId || entityTag.tag?.id;
      if (didSaveTagValue && savedTagId) {
        updateTagValueInCache(client.cache, {
          tagId: savedTagId,
          attributes: updatedAttributes,
          displayValue,
          entityId,
          tagTypes,
          entityType,
        });
      }

      setIsSaving(false);
      onClose();
    } catch (e) {
      setError(e);
      setIsSaving(false);
    }
  };

  const { appliedCount } = entityTag;

  const canEditEntityTagAttributes =
    !readOnly &&
    uniqueAttrTypes.some(
      (attrType) => attrType.action !== TAG_ATTRIBUTE_ACTION.READONLY
    );
  const canSave = includeTagLevelAttributes || canEditEntityTagAttributes;

  const modalHeader = (
    <Flex px="xs">
      <Flex width="50%" alignItems="center" justifyContent="space-between">
        <H3>Tag details</H3>
        {appliedCount > 0 && (
          <Flex alignItems="center">
            <Popover variant="tooltip" />
            <Small ml="s">{`Tag applied ${appliedCount} ${pluralize(
              "time",
              appliedCount
            )}`}</Small>
          </Flex>
        )}
      </Flex>
      <Box width="50%">
        <H3 ml="19px">Tag attributes</H3>
      </Box>
    </Flex>
  );
  const modalFooter = (
    <Flex pl="4px">
      <Button
        onClick={onSave}
        isLoading={isSaving}
        disabled={isSaving || !canSave}
        variant={isSaving || !canSave ? "disabled" : "success"}
        mr="s"
      >
        Save
      </Button>
      <Button onClick={onClose} variant="ghost">
        Close
      </Button>
    </Flex>
  );

  const allAttributes = {
    ...updatedAttributes,
    ...updatedEntityTagAttributes,
  };

  return (
    <Modal
      width={["90vw", "90vw", "90vw", "80vw", "70vw", "50vw"]}
      height={["80vh", "80vh", "80vh", "80vh", "60vh"]}
      maxWidth="90vw"
      maxHeight="90vh"
      visible
      onClose={onClose}
      headerContent={modalHeader}
      footerContent={modalFooter}
    >
      <Flex width="100%" p="xs" pt="r" flexDirection="column">
        {requiredFieldsMessage && (
          <Notification
            closable={false}
            variant="error"
            icon="exclamation-triangle"
            mb="r"
          >
            {requiredFieldsMessage}
          </Notification>
        )}
        <Flex width="100%">
          <Box mr="r" width="50%">
            <Flex flexDirection="column" gap="r">
              <AdjustedTextInput
                name="tag-type"
                type="text"
                id="tagType"
                label="Tag type"
                fullWidth
                value={tagTypeName}
                disabled
              />
              {!isDisplayValueEditable && (
                <AdjustedTextInput
                  data-testid="tag-display-input"
                  name="tag-display-value"
                  type="text"
                  id="tag-value-textinput"
                  label="Tag display value"
                  fullWidth
                  value={displayValue}
                  disabled
                />
              )}

              {hasDetails && (
                <Box>
                  <P mb="4px">Tag type description</P>
                  <DetailsWrapper p="s" boxBorder="default" borderRadius={2}>
                    <RichTextContentViewer
                      content={parsedDetails}
                      fontSize="14px"
                    />
                  </DetailsWrapper>
                </Box>
              )}
              <TagAttributesFields
                attrTypeValues={readonlyAttrTypes}
                updatedAttributes={allAttributes}
                onAttrValueChange={onAttrValueChange}
                onSelectAttrValue={onSelectAttrValue}
                onDateChange={onDateChange}
                featureFlags={featureFlags}
                tagTypesConfig={tagTypesConfig}
                editMode={includeTagLevelAttributes}
              />
            </Flex>
          </Box>
          <Box width="50%">
            <Notification
              closable={false}
              variant="warning"
              icon="exclamation-triangle"
              mb="r"
            >
              Values changed below will apply to ALL people / teams that have
              this tag.
            </Notification>
            {isDisplayValueEditable && (
              <AdjustedTextInput
                data-testid="tag-display-input"
                name="tag-display-value"
                type="text"
                id="tag-value-name"
                label="Tag display value"
                fullWidth
                value={displayValue}
                onChange={onDisplayValueChange}
                mandatory
                disabled={!includeTagLevelAttributes}
              />
            )}
            <TagAttributesFields
              attrTypeValues={editableAttrTypes}
              updatedAttributes={allAttributes}
              onAttrValueChange={onAttrValueChange}
              onSelectAttrValue={onSelectAttrValue}
              onDateChange={onDateChange}
              fieldToBeFocused={fieldToBeFocused}
              fieldsToShowInvalidity={fieldsToShowInvalidity}
              featureFlags={featureFlags}
              tagTypesConfig={tagTypesConfig}
              editMode={includeTagLevelAttributes}
            />
          </Box>
          {error && (
            <ErrorNotification message={error?.message} error={error} />
          )}
        </Flex>
      </Flex>
      {!isEmpty(uniqueAttrTypes) && (
        <Flex width="100%" p="xs" pt="r" flexDirection="column">
          <Divider light />
          <Box width="50%" mt="r">
            <Box mb="r">
              <H3>Unique attributes</H3>
              Note: Changing these will update these values only for this
              entity.
            </Box>
            <Notification closable={false} icon="info-circle" mb="r">
              Values changed below will apply to ONLY this person / team that
              has this tag.
            </Notification>
            <TagAttributesFields
              attrTypeValues={uniqueAttrTypes}
              updatedAttributes={allAttributes}
              onAttrValueChange={onAttrValueChange}
              onSelectAttrValue={onSelectAttrValue}
              onDateChange={onDateChange}
              fieldToBeFocused={fieldToBeFocused}
              fieldsToShowInvalidity={fieldsToShowInvalidity}
              featureFlags={featureFlags}
              tagTypeConfig={tagTypeConfig}
              editMode={editMode || canEditEntityTagAttributes}
            />
          </Box>
        </Flex>
      )}
      {parentTag && parentTag.length > 0 && (
        <Flex width="100%" p="xs" pt="r" flexDirection="column">
          <Divider light />
          <Box width="50%" mt="r">
            <Box mb="r">
              <H3>Parent tags</H3>
            </Box>
            <TagAttributesFields
              attrTypeValues={parentTag}
              updatedAttributes={allAttributes}
              onAttrValueChange={onAttrValueChange}
              onSelectAttrValue={onSelectAttrValue}
              onDateChange={onDateChange}
              fieldToBeFocused={fieldToBeFocused}
              fieldsToShowInvalidity={fieldsToShowInvalidity}
              featureFlags={featureFlags}
              tagTypesConfig={tagTypesConfig}
              editMode={includeTagLevelAttributes}
            />
          </Box>
        </Flex>
      )}
    </Modal>
  );
};

TagAttributesModal.propTypes = {
  onClose: PropTypes.func,
  entityTag: PropTypes.object,
  tagTypeConfig: PropTypes.object,
  isNewTag: PropTypes.bool,
  setNewTag: PropTypes.func,
  comments: PropTypes.object,
  entityId: PropTypes.string,
  tagTypes: PropTypes.arrayOf(PropTypes.string),
  featureFlags: PropTypes.object,
  tagTypesConfig: PropTypes.array,
  editMode: PropTypes.bool,
  readOnly: PropTypes.bool,
  entityType: PropTypes.string,
  onTagUpdated: PropTypes.func,
};

export default TagAttributesModal;
